import styles from "~/sass/organisms/Article/Article.module.scss";
import Head from "~/components/Meta/Head";
import ArticleAuthor from "~/components/molecules/Article/ArticleAuthor.js";
import Script from "next/script";

export default function RealEstateHistoryArticle({}) {
  const content = () => {
    return (
      <>
        <h1 className={styles["Article__heading"]}>
          Real Estate History: All About the Louisiana Purchase
        </h1>
        <ArticleAuthor />
        <p>
          Thomas Jefferson made a real estate decision while president that
          doubled the size of the United States, redefined the understood
          meaning of the doctrine of implied powers written into the
          Constitution, and changed the path of American history. It's also
          considered one of the greatest deals in real estate history,
          considering that when he made the purchase in 1803, about 828,000
          square miles of land were secured for about three cents an acre.
          Jefferson made this deal with France, which held the land at the time.
        </p>
        <h2 className={styles["Article__sub-heading"]}>
          The Louisiana Territory Under French and Spanish Rule
        </h2>
        <p>
          France gained control of what was then known as the Louisiana
          Territory through exploration and settlement throughout the 17th and
          18th centuries. However, it wasn't easy for France to maintain control
          of the territory. They lost control over this land as a result of the
          Seven Years' War, which led to France giving Spain control of the area
          west of the Mississippi and Great Britain control of their other land
          holdings and settlements in North America. When Napoleon Bonaparte
          took over leadership of France, he set his sights on regaining the
          country's colonial holdings. In October 1800, Spain agreed to give
          control of the Louisiana Territory back to France under the Treaty of
          San Ildefonso, with the understanding that France would never cede
          control of the area to any other country. The American government
          wasn't happy with this: The young country was expanding westward, and
          the port of New Orleans and the Mississippi River were quickly growing
          into huge resources for economic development for the country. A 1795
          treaty with Spain gave the U.S. access to the Mississippi River and
          its ports without paying duties, but that agreement was showing strain
          by 1800.
        </p>
        <h2 className={styles["Article__sub-heading"]}>
          Negotiations With France
        </h2>
        <p>
          Robert R. Livingston was the U.S. ambassador stationed in Paris when
          France took control of the Louisiana Territory. Jefferson ordered
          Livingston to approach Napoleon's government about the territory. The
          initial overtures didn't go well, but Livingston had one very good
          card to play: Napoleon didn't want the United States and Great Britain
          to form an alliance, and at that moment, the two countries were
          reestablishing a relationship. Napoleon was staring down the fact that
          war with Great Britain was imminent, and he didn't want the United
          States to side with the British. James Monroe sailed to France to help
          with the negotiations, and soon, a very large real estate deal was
          brewing between the two countries.
        </p>
        <h2 className={styles["Article__sub-heading"]}>
          Defining the Louisiana Purchase
        </h2>
        <p>
          The treaty between the two countries that defined the sale of the
          territory was signed on May 2, 1803, but backdated to April 30. The
          United States paid France $11,250,000 and assumed outstanding debts of
          $3,750,000. After interest was added in, the United States paid
          $27,267,622 for the territory. In exchange, America took over the
          entirety of the Louisiana Territory that France had gotten from Spain.
          However, unlike in modern real estate contracts, the wording that
          described exactly what the United States was receiving for its money
          was unclear. There were no definite descriptions of property or
          boundaries, and control of areas like the western part of Florida was
          left up in the air. Monroe and Livingston were aware of these issues,
          but unclear boundaries weren't the biggest issue facing the sale.
          There were serious questions about whether Jefferson had had the
          constitutional authority to let Monroe and Livingston make this deal.
          It would have to go before the Senate for approval, as all other
          treaties did. There was a lot of debate and consideration, but the
          Senate finally confirmed the treaty by a vote of 24 to 7.
        </p>
        <p>
          Once the deal was approved, the government now had to establish
          exactly what land they now controlled. This required negotiations with
          both Great Britain and Spain. It wasn't until Florida was purchased
          from Spain in 1819 that the most vexing border questions were settled.
          However, the general consensus was that the Louisiana Territory ran
          from the Mississippi River to the east to the Rocky Mountains to the
          west. Its northern border was firmly established by an 1818 convention
          between the United States and Great Britain. The territory was
          resource-rich and helped fund the economic development of the United
          States in the 19th century.
        </p>
        <ul>
          <li>
            <a
              rel="nofollow"
              href="https://www.monticello.org/thomas-jefferson/louisiana-lewis-clark/the-louisiana-purchase/"
            >
              The Louisiana Purchase
            </a>
          </li>
          <li>
            <a
              rel="nofollow"
              href="https://history.state.gov/milestones/1801-1829/louisiana-purchase"
            >
              Louisiana Purchase, 1803
            </a>
          </li>
          <li>
            <a
              rel="nofollow"
              href="https://www.smithsonianmag.com/history/how-the-louisiana-purchase-changed-the-world-79715124/"
            >
              How the Louisiana Purchase Changed the World
            </a>
          </li>
          <li>
            <a
              rel="nofollow"
              href="https://www.archives.gov/milestone-documents/louisiana-purchase-treaty"
            >
              Louisiana Purchase Treaty
            </a>
          </li>
          <li>
            <a rel="nofollow" href="https://www.ushistory.org/us/20c.asp">
              Westward Expansion: The Louisiana Purchase
            </a>
          </li>
          <li>
            <a
              rel="nofollow"
              href="https://constitutioncenter.org/blog/the-louisiana-purchase-jeffersons-constitutional-gamble"
            >
              The Louisiana Purchase: Jefferson's Constitutional Gamble
            </a>
          </li>
          <li>
            <a
              rel="nofollow"
              href="https://www.loc.gov/collections/louisiana-european-explorations-and-the-louisiana-purchase/articles-and-essays/a-question-of-boundaries/"
            >
              A Question of Boundaries
            </a>
          </li>
          <li>
            <a
              rel="nofollow"
              href="https://education.nationalgeographic.org/resource/louisiana-purchased"
            >
              March 10, 1804: The Louisiana Purchase Is Completed
            </a>
          </li>
          <li>
            <a
              rel="nofollow"
              href="https://www.senate.gov/about/powers-procedures/treaties/senate-approves-louisiana-purchase-treaty.htm"
            >
              The Senate Approves Ratification of the{" "}
            </a>
            <a href="https://www.realestateagents.com/la/new-orleans">
              Louisiana
            </a>
            <a
              rel="nofollow"
              href="https://www.senate.gov/about/powers-procedures/treaties/senate-approves-louisiana-purchase-treaty.htm"
            >
              {" "}
              Purchase Treaty
            </a>
          </li>
          <li>
            <a
              rel="nofollow"
              href="https://www.history.nd.gov/exhibits/lewisclark/lapurchase.html"
            >
              How and Why Did the United States Obtain the Louisiana Purchase?
            </a>
          </li>
          <li>
            <a
              rel="nofollow"
              href="https://www.khanacademy.org/humanities/us-history/the-early-republic/politics-society-early-19th-c/a/the-louisiana-purchase"
            >
              The Louisiana Purchase and Its Exploration
            </a>
          </li>
          <li>
            <a
              rel="nofollow"
              href="https://www.lpb.org/programs/americas-louisiana-purchase"
            >
              America's Louisiana Purchase
            </a>
          </li>
          <li>
            <a
              rel="nofollow"
              href="https://avalon.law.yale.edu/19th_century/louis1.asp"
            >
              Louisiana Purchase Treaty of 1803
            </a>
          </li>
          <li>
            <a
              rel="nofollow"
              href="https://www.billofrightsinstitute.org/e-lessons/jefferson-and-the-louisiana-purchase"
            >
              Jefferson and the Louisiana Purchase
            </a>
          </li>
          <li>
            <a
              rel="nofollow"
              href="https://oksenate.gov/education/senate-artwork/ceremonial-transfer-louisiana-purchase-new-orleans-1803"
            >
              Ceremonial Transfer of the Louisiana Purchase in New Orleans
            </a>
          </li>
          <li>
            <a href="https://www.realestateagents.com/la/baton-rouge">
              Louisiana
            </a>
            <a
              rel="nofollow"
              href="https://www.nps.gov/articles/000/louisiana-territory-officially-transferred.htm"
            >
              {" "}
              Territory Officially Transferred
            </a>
          </li>
        </ul>
      </>
    );
  };

  return (
    <>
      <Head
        title={
          "Real Estate History: All About the Louisiana Purchase | RealEstateAgents.com"
        }
        description={
          "The Louisiana Purchase changed the course and size of the United States forever. Dive into one of the country's biggest real estate deals."
        }
      />
      <article className={styles["Article"]}>
        <div className={styles["Article__container"]}>{content()}</div>
      </article>
      <Script strategy="beforeInteractive" type="application/ld+json">
        {{
          "@context": "https://schema.org",
          "@type": "Article",
          mainEntityOfPage: {
            "@type": "WebPage",
            "@id":
              "https://www.realestateagents.com/articles/real-estate-history-louisiana-purchase",
          },
          headline: "Real Estate History: All About the Louisiana Purchase",
          author: {
            "@type": "Person",
            name: "Amber Martinez",
            jobTitle: "Education Writer",
            sameAs: [
              "https://www.realestateagents.com/articles/author/amber-martinez",
              "https://www.linkedin.com/in/amber-martinez-7096651a/",
            ],
          },
        }}
      </Script>
    </>
  );
}
